import React, { useState } from 'react';
import { Flex, Text } from '@mantine/core';
import styles from './CompactCalendarButton.module.scss';

interface MeetingDetails {
  name: string;
  startDate: string;
  startTime: string;
  endTime: string;
  location?: string;
  description?: string;
  timeZone?: string;
}

interface CompactCalendarButtonProps {
  meetingDetails: MeetingDetails;
  isCalendarAdded?: boolean;
  onCalendarAdded?: (provider: string) => Promise<void>;
}

// Brand icon SVG components (compact versions)
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const OutlookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M7 4a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7z" fill="#0078D4"/>
    <path d="M12 7.5c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 9 12 9s2.5 1.12 2.5 2.5S13.38 14 12 14z" fill="white"/>
  </svg>
);

const Office365Icon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M20 3H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" fill="#D83B01"/>
    <path d="M4 7h2v10H4V7z" fill="#D83B01" opacity="0.7"/>
    <path d="M2 9h2v6H2V9z" fill="#D83B01" opacity="0.5"/>
  </svg>
);

const YahooIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.28 5.23l-3.53 7.83-3.54-7.83H2.77l5.46 11.39v6.15h3.54v-6.15l5.46-11.39h-3.95zM21.23 11.39h-3.54v-6.16h3.54v6.16z"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const CompactCalendarButton: React.FC<CompactCalendarButtonProps> = ({
  meetingDetails,
  isCalendarAdded = false,
  onCalendarAdded
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [clickedProvider, setClickedProvider] = useState<string | null>(null);

  const generateCalendarUrl = (provider: string): string => {
    const { name, startDate, startTime, endTime, location, description, timeZone } = meetingDetails;
    const startDateTime = `${startDate}T${startTime}:00`;
    const endDateTime = `${startDate}T${endTime}:00`;

    switch (provider) {
      case 'google':
        const googleParams = new URLSearchParams({
          action: 'TEMPLATE',
          text: name,
          dates: `${startDate.replace(/-/g, '')}T${startTime.replace(':', '')}00/${startDate.replace(/-/g, '')}T${endTime.replace(':', '')}00`,
          details: description || '',
          location: location || '',
          ctz: timeZone || 'America/New_York'
        });
        return `https://calendar.google.com/calendar/render?${googleParams}`;

      case 'outlook':
        const outlookParams = new URLSearchParams({
          path: '/calendar/action/compose',
          rru: 'addevent',
          subject: name,
          startdt: startDateTime,
          enddt: endDateTime,
          body: description || '',
          location: location || ''
        });
        return `https://outlook.live.com/calendar/0/deeplink/compose?${outlookParams}`;

      case 'office365':
        const office365Params = new URLSearchParams({
          path: '/calendar/action/compose',
          rru: 'addevent',
          subject: name,
          startdt: startDateTime,
          enddt: endDateTime,
          body: description || '',
          location: location || ''
        });
        return `https://outlook.office.com/calendar/0/deeplink/compose?${office365Params}`;

      case 'yahoo':
        const yahooParams = new URLSearchParams({
          v: '60',
          title: name,
          st: startDateTime,
          et: endDateTime,
          desc: description || '',
          in_loc: location || ''
        });
        return `https://calendar.yahoo.com/?${yahooParams}`;

      case 'apple':
      case 'ical':
        return generateICSFile(meetingDetails);

      default:
        return '';
    }
  };

  const generateICSFile = (details: MeetingDetails): string => {
    const { name, startDate, startTime, endTime, location, description } = details;
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Sales CRM//Calendar Event//EN',
      'BEGIN:VEVENT',
      `DTSTART:${startDate.replace(/-/g, '')}T${startTime.replace(':', '')}00`,
      `DTEND:${startDate.replace(/-/g, '')}T${endTime.replace(':', '')}00`,
      `SUMMARY:${name}`,
      `DESCRIPTION:${description || ''}`,
      `LOCATION:${location || ''}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    return URL.createObjectURL(blob);
  };

  const handleCalendarClick = async (provider: string) => {
    const url = generateCalendarUrl(provider);

    if (provider === 'apple' || provider === 'ical') {
      const link = document.createElement('a');
      link.href = url;
      link.download = 'meeting.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(url, '_blank', 'width=600,height=600');
    }

    // Mark this provider as clicked (turns green)
    setClickedProvider(provider);

    // Trigger backend save callback
    if (onCalendarAdded) {
      try {
        await onCalendarAdded(provider);
      } catch (error) {
        console.error('Failed to save calendar confirmation:', error);
      }
    }

    // Close menu after a brief delay to show the green feedback
    setTimeout(() => {
      setShowMenu(false);
    }, 800);
  };

  const calendarOptions = [
    { provider: 'google', name: 'Google', IconComponent: GoogleIcon },
    { provider: 'apple', name: 'Apple', IconComponent: AppleIcon },
    { provider: 'outlook', name: 'Outlook', IconComponent: OutlookIcon },
    { provider: 'office365', name: 'Office 365', IconComponent: Office365Icon },
    { provider: 'yahoo', name: 'Yahoo', IconComponent: YahooIcon },
    { provider: 'ical', name: 'Other', IconComponent: CalendarIcon },
  ];

  return (
    <div className={styles.compactContainer}>
      <button
        onClick={() => setShowMenu(!showMenu)}
        className={`${styles.mainButton} ${isCalendarAdded ? styles.added : ''}`}
        title={isCalendarAdded ? 'Already added. Click to add again or export to another calendar.' : 'Add to Calendar'}
      >
        <Text size="12px" fw={500}>
          {isCalendarAdded ? '✓ Added to Calendar' : '📅 Add to Calendar'}
        </Text>
      </button>

      {showMenu && (
        <>
          <div className={styles.overlay} onClick={() => setShowMenu(false)} />
          <div className={styles.menu}>
            {calendarOptions.map(({ provider, name, IconComponent }) => {
              const isClicked = clickedProvider === provider;

              return (
                <button
                  key={provider}
                  onClick={() => handleCalendarClick(provider)}
                  className={`${styles.menuItem} ${isClicked ? styles.menuItemClicked : ''}`}
                >
                  <Flex align="center" gap={10}>
                    <div className={styles.iconWrapper}>
                      <IconComponent />
                    </div>
                    <Text size="13px" fw={500} c={isClicked ? '#16A34A' : undefined}>
                      {isClicked ? `✓ Added to ${name}` : name}
                    </Text>
                  </Flex>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default CompactCalendarButton;
